import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { UPLOAD_URL } from "../utils/constants";
import { useLanguage } from "../context/LanguageContext";
import { getRequest } from "../helpers/api.helper";

type ShareBiodataModalProps = {
  setIsShareBiodataModalOpen: (open: boolean) => void;
  profile: any;
  shareType: "pdf" | "image";
};

const frames = Array.from({ length: 20 }, (_, i) => ({
  id: i + 1,
  name: `Frame ${i + 1}`,
  preview: `/frames/frame${i + 1}.jpeg`,
  image: `/frames/frame${i + 1}.jpeg`,
}));

const uploadsBase = UPLOAD_URL.replace(/\/+$/, "");
const getFallbackPhoto = () =>
  typeof window !== "undefined"
    ? `${window.location.origin}/frames/sample-user.jpeg`
    : "/frames/sample-user.jpeg";

const normalizePhoto = (source: string) => {
  const trimmed = source.replace(/^\/+/, "");
  if (!trimmed) return getFallbackPhoto();
  if (trimmed.startsWith("uploads/")) {
    return `${uploadsBase}/${trimmed.replace(/^uploads\//, "")}`;
  }
  return `${uploadsBase}/users/profile_pic/${trimmed}`;
};

const resolveProfilePhoto = (record?: Record<string, any> | null): string => {
  if (!record) return getFallbackPhoto();
  const photoCandidates = [
    record.profileImage,
    record.image,
    record.media?.image,
    Array.isArray(record.gallery) ? record.gallery[0] : undefined,
  ];

  for (const candidate of photoCandidates) {
    const text = ensureText(candidate);
    if (text) {
      return /^https?:\/\//i.test(text) ? text : normalizePhoto(text);
    }
  }
  return getFallbackPhoto();
};

const ensureText = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const pickFirstValue = (value: unknown): unknown => {
  if (!Array.isArray(value)) return value;
  for (const entry of value) {
    const text = ensureText(entry);
    if (text) return text;
  }
  return undefined;
};

const toTitleCase = (value: string): string =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token[0].toUpperCase() + token.slice(1).toLowerCase())
    .join(" ");

const formatValue = (value: unknown, fallback = "No Data") => {
  const resolved = Array.isArray(value)
    ? value.map(ensureText).filter((entry): entry is string => Boolean(entry))
    : ensureText(value);
  if (Array.isArray(resolved)) {
    const joined = resolved.join(", ");
    return joined || fallback;
  }
  if (!resolved) return fallback;
  return resolved;
};

const formatTitleValue = (value: unknown, fallback = "No Data") => {
  const text = formatValue(value, "");
  if (!text) return fallback;
  return toTitleCase(text);
};

const hasMeaningfulOptionalValue = (value: unknown): boolean => {
  const text = ensureText(value);
  if (!text) return false;
  const numericValue = Number(text);
  if (Number.isFinite(numericValue)) {
    return numericValue > 0;
  }
  return text !== "No Data";
};

const renderableItems = <T extends { value: string }>(items: T[]): T[] =>
  items.filter((item) => hasMeaningfulOptionalValue(item.value));

const formatDateValue = (value?: string | null, fallback = "No Data") => {
  if (!value) return fallback;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return formatValue(value, fallback);
  }
  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const calculateAge = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  const diff = Date.now() - parsed.getTime();
  if (diff < 0) return undefined;
  const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  if (years < 0 || years > 120) return undefined;
  return `${years} years`;
};

const buildLocationString = (record?: Record<string, any>): string => {
  if (!record) return "No Data";
  const parts = [
    record.originalLocation,
    record.location,
    record.city,
    record.district,
    record.state,
    record.country,
  ]
    .map((part) => formatValue(part, ""))
    .filter(Boolean) as string[];
  return parts.join(", ") || "No Data";
};

const clampText = (value: string, limit = 28): string => {
  if (value.length <= limit) return value;
  return `${value.slice(0, limit - 1)}…`;
};

const ShareBiodataModal = ({
  setIsShareBiodataModalOpen,
  profile,
  shareType,
}: ShareBiodataModalProps) => {
  const [selectedFrame, setSelectedFrame] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [previewProfile, setPreviewProfile] = useState(profile);

  const biodataImageRef = useRef<HTMLDivElement | null>(null);
  const { t } = useLanguage();

  useEffect(() => {
    setPreviewProfile(profile);
  }, [profile?.martialId]);

  const loadImageAsBase64 = async (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            reject(new Error("Canvas context not available"));
            return;
          }

          ctx.drawImage(img, 0, 0);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
          resolve(dataUrl);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = (error) => {
        console.error("Image load failed for URL:", url, error);
        reject(new Error(`Failed to load image: ${url}`));
      };

      img.src = url;
    });
  };

  const fetchProfile = async () => {
    const response = await getRequest(
      `/bureau/profile/${profile.martialId || profile.maritalId}`
    );
    if (!response?.success) {
      throw new Error(response?.message || "Failed to load profile");
    }
    return response.data;
  };

  const makeRoundedPng = (srcDataUrl: string, w = 400, h = 440, r = 80) => {
    return new Promise<string>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject("Canvas not supported");

        ctx.clearRect(0, 0, w, h);

        ctx.beginPath();
        // @ts-ignore
        if (ctx.roundRect) {
          ctx.roundRect(0, 0, w, h, r);
        } else {
          ctx.rect(0, 0, w, h);
        }
        ctx.closePath();
        ctx.clip();

        const scale = Math.max(w / img.width, h / img.height);
        const nw = img.width * scale;
        const nh = img.height * scale;
        const nx = (w - nw) / 2;
        const ny = (h - nh) / 2;

        ctx.drawImage(img, nx, ny, nw, nh);

        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = () => reject("Image load failed");
      img.src = srcDataUrl;
    });
  };

  const sharePdfWithFrame = async (frame: any) => {
    setLoading(true);
    try {
      const data = await fetchProfile();

      if (!data) {
        throw new Error("Profile data is empty or undefined");
      }
      if (!frame?.image) {
        throw new Error("Frame image missing");
      }

      const frameUrl = window.location.origin + frame.image;

      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const cardWidth = pageWidth - margin * 2;
      const columnGap = 8;
      const columnWidth = (cardWidth - columnGap) / 2;
      const statusCols = 3;
      const statusGap = 4;
      const statusHeight = 18;

      const displayName =
        ensureText(data?.fullName) ||
        ensureText(
          [data?.firstName, data?.lastName].filter(Boolean).join(" ").trim()
        ) ||
        "Biodata";
      const idValue = formatValue(data?.martialId ?? data?._id, "N/A");
      const locationText = buildLocationString(data);

      const headerStats = [
        { label: "ID", value: idValue },
        {
          label: "Payment",
          value: formatTitleValue(
            pickFirstValue(
              data?.paymentStatus ?? data?.planType ?? data?.accountType
            ),
            "Free Profile"
          ),
        },
        {
          label: "Citizenship",
          value: formatTitleValue(
            pickFirstValue(data?.citizenship ?? data?.citizenshipPreferences),
            "Indian profile"
          ),
        },
        {
          label: "Service Preference",
          value: formatTitleValue(
            pickFirstValue(data?.partnerServicePreference),
            "Undefined"
          ),
        },
        {
          label: "Created By",
          value: formatTitleValue(
            pickFirstValue(data?.createdBy ?? data?.profileCreatedBy),
            "Self profile"
          ),
        },
        {
          label: "Caste Preference",
          value: formatTitleValue(
            pickFirstValue(data?.castePreferences) ?? data?.caste,
            "No Data"
          ),
        },
      ].map((stat) => ({
        ...stat,
        value: clampText(stat.value),
      }));

      const personalItems = [
        { label: "Name", value: formatValue(displayName) },
        { label: "Date Of Birth", value: formatDateValue(data?.dateOfBirth) },
        {
          label: "Age",
          value: calculateAge(data?.dateOfBirth) ?? "No Data",
          color: "red",
        },
        { label: "Birth Time", value: formatValue(data?.time) },
        {
          label: "Marital Status",
          value: formatTitleValue(data?.maritalStatus),
        },
        {
          label: "Physical State",
          value: formatTitleValue(data?.physicalState),
        },
        { label: "Education", value: formatValue(data?.education) },
        {
          label: "Employment Status",
          value: formatValue(data?.employmentStatus),
        },
        { label: "Occupation", value: formatValue(data?.occupation) },
        { label: "Annual Income", value: formatValue(data?.annualIncome) },
        { label: "Location", value: locationText },
      ];

      const familyItems = [
        { label: "Family Type", value: formatTitleValue(data?.familyType) },
        { label: "Family Value", value: formatTitleValue(data?.familyValue) },
        {
          label: "Father Employee",
          value: formatTitleValue(data?.fatherEmployee),
        },
        {
          label: "Father Occupation",
          value: formatValue(data?.fatherOccupation),
        },
        {
          label: "Mother Employee",
          value: formatTitleValue(data?.motherEmployee),
        },
        {
          label: "Mother Occupation",
          value: formatValue(data?.motherOccupation),
        },
        { label: "Total Brothers", value: formatValue(data?.totalBrothers) },
        {
          label: "Married Brothers",
          value: formatValue(data?.marriedBrothers),
        },
        { label: "Total Sisters", value: formatValue(data?.totalSisters) },
        { label: "Married Sisters", value: formatValue(data?.marriedSisters) },
      ].filter((item) => hasMeaningfulOptionalValue(item.value));

      const starText = ensureText(data?.star);
      const raasiText = ensureText(data?.raasi);
      const starRaasi =
        [starText, raasiText].filter(Boolean).join(" / ") || "No Data";

      const religionItems = [
        { label: "Religion", value: formatTitleValue(data?.religion) },
        { label: "Caste", value: formatTitleValue(data?.caste) },
        { label: "Sub Caste", value: formatTitleValue(data?.subcaste) },
        { label: "Mother Tongue", value: formatValue(data?.motherTongue) },
        { label: "Gotram", value: formatValue(data?.gotram) },
        { label: "Star / Raasi", value: starRaasi },
      ];

      let bgBase64: string;
      try {
        bgBase64 = await loadImageAsBase64(frameUrl);
      } catch (error) {
        console.error("Failed to load frame image, using fallback:", error);
        const canvas = document.createElement("canvas");
        canvas.width = pageWidth * 10;
        canvas.height = pageHeight * 10;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        bgBase64 = canvas.toDataURL("image/jpeg");
      }

      const fallbackPhoto = getFallbackPhoto();
      const resolvedPhoto = resolveProfilePhoto(data);

      let photoBase64: string;
      try {
        photoBase64 = await loadImageAsBase64(resolvedPhoto);
      } catch (error) {
        console.error("Failed to load profile photo, using fallback:", error);
        try {
          photoBase64 = await loadImageAsBase64(fallbackPhoto);
        } catch (fallbackError) {
          console.error("Failed to load fallback photo:", fallbackError);
          const canvas = document.createElement("canvas");
          canvas.width = 400;
          canvas.height = 440;
          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.fillStyle = "#e0e0e0";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = "#666";
            ctx.font = "30px Arial";
            ctx.textAlign = "center";
            ctx.fillText("No Photo", canvas.width / 2, canvas.height / 2);
          }
          photoBase64 = canvas.toDataURL("image/jpeg");
        }
      }

      doc.addImage(bgBase64, "JPEG", 0, 0, pageWidth, pageHeight);

      const font = "helvetica";
      const statusRows = Math.ceil(headerStats.length / statusCols);
      const headerHeight = 12 + statusRows * (statusHeight + statusGap);

      const imgX = margin;
      const imgY = margin + 8;
      const imgW = 40;
      const imgH = 44;

      const roundedPng = await makeRoundedPng(photoBase64, 400, 440, 80);

      doc.addImage(roundedPng, "PNG", imgX, imgY, imgW, imgH);

      doc.setDrawColor(255, 193, 7);
      doc.setLineWidth(0.4);
      doc.roundedRect(imgX, imgY, imgW, imgH, 8, 8, "S");

      const statusAreaWidth = cardWidth - 52;
      const statusWidth =
        (statusAreaWidth - (statusCols - 1) * statusGap) / statusCols;
      const statusStartY = margin + 10;

      headerStats.forEach((stat, index) => {
        const col = index % statusCols;
        const row = Math.floor(index / statusCols);
        const x = margin + 52 + col * (statusWidth + statusGap);
        const y = statusStartY + row * (statusHeight + statusGap);

        doc.setFillColor(255, 247, 234);
        doc.setDrawColor(255, 193, 7);
        doc.setLineWidth(0.3);
        doc.roundedRect(x, y, statusWidth, statusHeight, 3, 3, "FD");
        doc.setFont(font, "bold");
        doc.setFontSize(8);
        doc.setTextColor(200, 120, 0);
        doc.text(stat.label, x + 3, y + 7);
        doc.setFont(font, "normal");
        doc.setTextColor(65, 40, 5);
        doc.text(stat.value, x + 3, y + 13);
      });

      const contentStartY = margin + headerHeight + 10;

      const renderDetailSection = (
        title: string,
        items: { label: string; value: string; color?: string }[],
        startX: number,
        startY: number,
        width: number
      ) => {
        let y = startY;

        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(255, 193, 7);
        doc.text(title, startX, y);
        y += 7;

        doc.setFontSize(11);
        doc.setTextColor(40, 40, 40);

        items.forEach((item) => {
          const displayValue = item.value?.trim() || "No Data";

          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text(`${item.label}:`, startX, y);

          doc.setFont("helvetica", "normal");
          if (item.color === "red") {
            doc.setTextColor(200, 0, 0);
          } else {
            doc.setTextColor(50, 50, 50);
          }

          const textX = startX + 35;
          const wrapped = doc.splitTextToSize(displayValue, width - 35);
          doc.text(wrapped, textX, y);

          y += 6 + (wrapped.length - 1) * 5;
        });

        return y + 4;
      };

      const leftBottom = renderDetailSection(
        "Personal Details",
        personalItems,
        margin,
        contentStartY,
        columnWidth
      );

      const rightBottom = familyItems.length
        ? renderDetailSection(
            "Family Details",
            familyItems,
            margin + columnWidth + columnGap,
            contentStartY,
            columnWidth
          )
        : contentStartY;

      let nextY = Math.max(leftBottom, rightBottom) + 10;

      renderDetailSection(
        "Religion And Caste",
        religionItems,
        margin,
        nextY,
        cardWidth
      );

      doc.setFont(font, "italic");
      doc.setFontSize(9);
      doc.setTextColor(110, 110, 110);
      doc.text(`Frame: ${frame.name}`, margin, pageHeight - margin);

      const fileName = `${displayName || "Biodata"}.pdf`;

      if (Capacitor.isNativePlatform()) {
        // Mobile: Save to filesystem and share
        const dataUri = doc.output("datauristring");
        const base64 = dataUri.split(",")[1];

        const saved = await Filesystem.writeFile({
          path: `MeeMarriage/${fileName}`,
          data: base64,
          directory: Directory.Documents,
          recursive: true,
        });

        toast.success("Sharing biodata PDF");

        await Share.share({
          title: "Biodata PDF",
          text: fileName,
          url: saved.uri,
        });
      } else {
        // Web: Direct share using Web Share API
        const pdfBlob = doc.output("blob");
        const file = new File([pdfBlob], fileName, { type: "application/pdf" });

        // Check if Web Share API is available and supports files
        if (
          navigator.share &&
          navigator.canShare &&
          navigator.canShare({ files: [file] })
        ) {
          try {
            await navigator.share({
              title: "Biodata PDF",
              text: `${displayName || "Biodata"}`,
              files: [file],
            });
            toast.success("PDF shared successfully");
          } catch (error: any) {
            // User cancelled or error occurred
            if (error.name !== "AbortError") {
              console.error("Share failed:", error);
              // Fallback to download
              doc.save(fileName);
              toast.info(
                "PDF downloaded. You can share it from your downloads folder."
              );
            }
          }
        } else {
          // Fallback: download the file
          doc.save(fileName);
          toast.info(
            "PDF downloaded. You can share it from your downloads folder."
          );
        }
      }
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e.message || "Failed to generate PDF"
      );
    } finally {
      setLoading(false);
    }
  };

  const shareImageWithFrame = async (frame: any) => {
    setLoading(true);
    try {
      const data = await fetchProfile();
      setSelectedFrame(frame.id);
      setPreviewProfile(data);

      await new Promise((r) => setTimeout(r, 120));

      if (!biodataImageRef.current) throw new Error("Preview missing");

      const canvas = await html2canvas(biodataImageRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        allowTaint: true,
        logging: false,
      });

      const fileName = `${data.fullName || "Biodata"}_Biodata.png`;
      const dataUrl = canvas.toDataURL("image/png");

      if (Capacitor.isNativePlatform()) {
        // Mobile: Save to filesystem and share
        const base64 = dataUrl.split(",")[1];

        const saved = await Filesystem.writeFile({
          path: `MeeMarriage/${fileName}`,
          data: base64,
          directory: Directory.Documents,
          recursive: true,
        });

        toast.success("Sharing biodata image");

        await Share.share({
          title: "Biodata Image",
          text: fileName,
          url: saved.uri,
        });
      } else {
        // Web: Direct share using Web Share API
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob) => {
            resolve(blob!);
          }, "image/png");
        });

        const file = new File([blob], fileName, { type: "image/png" });

        // Check if Web Share API is available and supports files
        if (
          navigator.share &&
          navigator.canShare &&
          navigator.canShare({ files: [file] })
        ) {
          try {
            await navigator.share({
              title: "Biodata Image",
              text: `${data.fullName || "Biodata"}`,
              files: [file],
            });
            toast.success("Image shared successfully");
          } catch (error: any) {
            // User cancelled or error occurred
            if (error.name !== "AbortError") {
              console.error("Share failed:", error);
              // Fallback to download
              const link = document.createElement("a");
              link.href = dataUrl;
              link.download = fileName;
              link.click();
              toast.info(
                "Image downloaded. You can share it from your downloads folder."
              );
            }
          }
        } else {
          // Fallback: download the file
          const link = document.createElement("a");
          link.href = dataUrl;
          link.download = fileName;
          link.click();
          toast.info(
            "Image downloaded. You can share it from your downloads folder."
          );
        }
      }
    } catch (e: any) {
      console.error("Image Share Error:", e);
      toast.error(e.message || "Failed to generate image");
    } finally {
      setLoading(false);
      setSelectedFrame(null);
    }
  };

  const onFrameClick = async (frame: any) => {
    if (shareType === "pdf") await sharePdfWithFrame(frame);
    if (shareType === "image") await shareImageWithFrame(frame);
    setIsShareBiodataModalOpen(false);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">
              Share Biodata as {shareType === "pdf" ? "PDF" : "Image"}
            </h2>
            <button
              onClick={() => setIsShareBiodataModalOpen(false)}
              className="text-2xl font-bold hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="mb-3">
            <h3 className="font-semibold">Select a Frame</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {frames.map((frame) => (
              <div
                key={frame.id}
                onClick={() => !loading && onFrameClick(frame)}
                className={`border rounded-lg p-2 cursor-pointer text-center hover:shadow-lg transition ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <img
                  src={frame.preview}
                  alt={frame.name}
                  className="w-full h-32 object-cover rounded-md mb-1"
                />
                <p className="font-medium text-xs text-gray-700 truncate">
                  {frame.name}
                </p>
              </div>
            ))}
          </div>

          {loading && (
            <div className="mt-4 text-center text-sm text-gray-600">
              {t("Generating your biodata, please wait")}...
            </div>
          )}
        </div>
      </div>

      {/* Hidden IMAGE preview */}
      {selectedFrame && (
        <div
          ref={biodataImageRef}
          style={{
            position: "fixed",
            top: "-9999px",
            left: "-9999px",
            width: "794px",
            height: "1123px",
            backgroundImage: `url(${
              frames.find((f) => f.id === selectedFrame)?.image
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            padding: "75px 75px",
            fontFamily: "Arial, sans-serif",
            color: "#333",
          }}
        >
          <div style={{ display: "flex", marginBottom: "20px" }}>
            <div style={{ marginRight: "20px" }}>
              <img
                src={resolveProfilePhoto(previewProfile)}
                alt="Profile"
                style={{
                  width: "150px",
                  height: "165px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
                crossOrigin="anonymous"
              />
            </div>

            <div
              style={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "10px",
                background:
                  "linear-gradient(135deg, rgba(255, 236, 209, 0.55), rgba(255, 220, 234, 0.7))",
                borderRadius: "14px",
                padding: "16px",
                boxShadow: "0 10px 25px rgba(255, 128, 171, 0.25)",
              }}
            >
              {[
                { label: "ID", value: previewProfile?.martialId || "N/A" },
                {
                  label: "Payment",
                  value: formatTitleValue(
                    previewProfile?.paymentStatus ?? previewProfile?.planType,
                    "Free Profile"
                  ),
                },
                {
                  label: "Citizenship",
                  value: formatTitleValue(
                    previewProfile?.citizenship,
                    "Indian profile"
                  ),
                },
                {
                  label: "Service Preference",
                  value: formatTitleValue(
                    previewProfile?.partnerServicePreference,
                    "Undefined"
                  ),
                },
                {
                  label: "Created By",
                  value: formatTitleValue(
                    previewProfile?.createdBy,
                    "Self profile"
                  ),
                },
                {
                  label: "Caste Preference",
                  value: formatTitleValue(
                    previewProfile?.castePreferences ?? previewProfile?.caste,
                    "No Data"
                  ),
                },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    border: "1px solid rgba(255, 193, 7, 0.35)",
                    borderRadius: "10px",
                    padding: "10px",
                    boxShadow: "0 6px 15px rgba(255, 193, 7, 0.18)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "9px",
                      fontWeight: "bold",
                      color: "#c07800",
                      marginBottom: "4px",
                    }}
                  >
                    {stat.label}
                  </div>
                  <div style={{ fontSize: "11px", color: "#3c3c3c" }}>
                    {clampText(stat.value)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#ffc107",
                  marginBottom: "10px",
                }}
              >
                Personal Details
              </h3>
              <div style={{ fontSize: "12px", lineHeight: "1.8" }}>
                {[
                  {
                    label: "Name",
                    value: formatValue(previewProfile?.fullName),
                  },
                  {
                    label: "Date Of Birth",
                    value: formatDateValue(previewProfile?.dateOfBirth),
                  },
                  {
                    label: "Age",
                    value:
                      calculateAge(previewProfile?.dateOfBirth) ?? "No Data",
                    color: "red",
                  },
                  {
                    label: "Birth Time",
                    value: formatValue(previewProfile?.time),
                  },
                  {
                    label: "Marital Status",
                    value: formatTitleValue(previewProfile?.maritalStatus),
                  },
                  {
                    label: "Physical State",
                    value: formatTitleValue(previewProfile?.physicalState),
                  },
                  {
                    label: "Education",
                    value: formatValue(previewProfile?.education),
                  },
                  {
                    label: "Employment Status",
                    value: formatValue(previewProfile?.employmentStatus),
                  },
                  {
                    label: "Occupation",
                    value: formatValue(previewProfile?.occupation),
                  },
                  {
                    label: "Annual Income",
                    value: formatValue(previewProfile?.annualIncome),
                  },
                  {
                    label: "Location",
                    value: buildLocationString(previewProfile),
                  },
                ].map((item, idx) => (
                  <div key={idx} style={{ marginBottom: "6px" }}>
                    <strong>{item.label}:</strong>{" "}
                    <span
                      style={{
                        color: item.color === "red" ? "#c80000" : "#323232",
                      }}
                    >
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "bold",
                  color: "#ffc107",
                  marginBottom: "10px",
                }}
              >
                Family Details
              </h3>
              <div style={{ fontSize: "12px", lineHeight: "1.8" }}>
                {renderableItems([
                  {
                    label: "Family Type",
                    value: formatTitleValue(previewProfile?.familyType),
                  },
                  {
                    label: "Family Value",
                    value: formatTitleValue(previewProfile?.familyValue),
                  },
                  {
                    label: "Father Employee",
                    value: formatTitleValue(previewProfile?.fatherEmployee),
                  },
                  {
                    label: "Father Occupation",
                    value: formatValue(previewProfile?.fatherOccupation),
                  },
                  {
                    label: "Mother Employee",
                    value: formatTitleValue(previewProfile?.motherEmployee),
                  },
                  {
                    label: "Mother Occupation",
                    value: formatValue(previewProfile?.motherOccupation),
                  },
                  {
                    label: "Total Brothers",
                    value: formatValue(previewProfile?.totalBrothers),
                  },
                  {
                    label: "Married Brothers",
                    value: formatValue(previewProfile?.marriedBrothers),
                  },
                  {
                    label: "Total Sisters",
                    value: formatValue(previewProfile?.totalSisters),
                  },
                  {
                    label: "Married Sisters",
                    value: formatValue(previewProfile?.marriedSisters),
                  },
                ]).map((item, idx) => (
                  <div key={idx} style={{ marginBottom: "6px" }}>
                    <strong>{item.label}:</strong>{" "}
                    <span style={{ color: "#323232" }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3
              style={{
                fontSize: "16px",
                fontWeight: "bold",
                color: "#ffc107",
                marginBottom: "10px",
              }}
            >
              Religion And Caste
            </h3>
            <div style={{ fontSize: "12px", lineHeight: "1.8" }}>
              {[
                {
                  label: "Religion",
                  value: formatTitleValue(previewProfile?.religion),
                },
                {
                  label: "Caste",
                  value: formatTitleValue(previewProfile?.caste),
                },
                {
                  label: "Sub Caste",
                  value: formatTitleValue(previewProfile?.subcaste),
                },
                {
                  label: "Mother Tongue",
                  value: formatValue(previewProfile?.motherTongue),
                },
                { label: "Gotram", value: formatValue(previewProfile?.gotram) },
                {
                  label: "Star / Raasi",
                  value:
                    [
                      ensureText(previewProfile?.star),
                      ensureText(previewProfile?.raasi),
                    ]
                      .filter(Boolean)
                      .join(" / ") || "No Data",
                },
              ].map((item, idx) => (
                <div key={idx} style={{ marginBottom: "6px" }}>
                  <strong>{item.label}:</strong>{" "}
                  <span style={{ color: "#323232" }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareBiodataModal;
